import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { editAgentConversationSchema } from '@/app/agentConversation/_validators';

import { Button } from '@/app/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/app/ui/dialog';
import { Error } from '@/app/ui/error';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/app/ui/form';
import { Input } from '@/app/ui/input';
import { Spinner } from '@/app/ui/spinner';
import { useAgentConversationObject, usePutAgentConversation } from '@/data/agentConversation';

const EditAgentConversationDialog = ({ uid, onClose, onSuccess }) => {
  const conversation = useAgentConversationObject(uid);
  const putAgentConversation = usePutAgentConversation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');

  const form = useForm({
    resolver: zodResolver(editAgentConversationSchema),
    defaultValues: { title: conversation.title }
  });

  const onSubmit = async (values) => {
    setApiError('');
    setIsSubmitting(true);
    try {
      const updated = await putAgentConversation(uid, values);
      await onSuccess(updated);
      onClose();
    } catch (error) {
      setIsSubmitting(false);
      setApiError(error.message);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Conversation</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} autoFocus data-testid="title-input" />
                  </FormControl>
                  <FormMessage data-testid="title-input-error" />
                </FormItem>
              )}
            />
            {apiError && <Error message={apiError} dataTestId="api-error" />}
            <DialogFooter>
              <Button type="submit" disabled={isSubmitting} data-testid="modal-submit-button">
                {isSubmitting && <Spinner />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default EditAgentConversationDialog;
